import React, { useCallback, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  TextField,
} from "@mui/material";
import {
  pushToHistoryAction,
  setDescription,
  setSubTitle,
  setWheelName,
  setCategories,
  setWheelTags,
  undo,
  redo,
} from "../../../../redux/reducers/segmentBuilder";
import {
  getDescription,
  getSubTitle,
  getWheelName,
  getCategories,
  getWheelTags,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { handleAiAssistantIcon } from "../../../FroalaEditor/children/FroalaHelpers";
import FroalaTextEditor from "../../../FroalaEditor/children/FroalaTextEditor";
import NewAIPrompt from "../../../NewAiPrompt";
import { getFroalaFocus } from "../../../../redux/selectors/segmentBuilderSelectors";
import { FROALA_FOCUS_ENUM } from "../../../../constants/FroalaFocus.enum";
import {
  WHEEL_CATEGORY_OPTIONS,
  MAX_WHEEL_CATEGORIES,
} from "../../../../constants/wheelCategories";

export { WHEEL_CATEGORY_OPTIONS, MAX_WHEEL_CATEGORIES };

export default function DetailsTab() {
  const editorRef = useRef(null);
  const editorRef2 = useRef(null);
  const editorRef3 = useRef(null);
  const wheel_name = useSelector(getWheelName);
  const sub_title = useSelector(getSubTitle);
  const description = useSelector(getDescription);
  const categories = useSelector(getCategories);
  const wheel_tags = useSelector(getWheelTags);
  const froalaFocus = useSelector(getFroalaFocus);
  const dispatch = useDispatch();

  const [tagInputValue, setTagInputValue] = useState("");

  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [currentCompletionType, setCurrentCompletionType] = useState("general");
  const [aiPromptPosition, setAiPromptPosition] = useState({
    top: "25vh",
    right: "35vw",
  });

  // Ref to store timeout ID for debounced state updates
  const debounceTimeoutRef = useRef(null);

  const handleFroalaCustomization = (FroalaEditor) => {
    handleAiAssistantIcon(
      FroalaEditor,
      setShowAiPrompt,
      (editorId, editorElement) => {
        // Set completion type based on which editor the AI assistant was clicked from
        let completionType = "general";
        switch (editorId) {
          case 1:
            completionType = "title";
            break;
          case 2:
            completionType = "subtitle";
            break;
          case 3:
            completionType = "description";
            break;
          default:
            completionType = "general";
        }
        setCurrentCompletionType(completionType);

        // Calculate position relative to the clicked editor
        if (editorElement) {
          const rect = editorElement.getBoundingClientRect();
          // Position the AI prompt to the right of the editor, aligned with the top
          // Add some offset for better visual appearance
          const top = Math.max(20, rect.top + window.scrollY - 100); // 50px offset from top
          const right = Math.max(20, window.innerWidth - rect.right + 350); // 350px offset from right

          setAiPromptPosition({
            top: `${top}px`,
            right: `${right}px`,
          });
        }
      }
    );
  };

  // Helper debounce function to avoid unnecessary state updates
  function debounce(func, wait) {
    return function (...args) {
      // Clear previous timeout if exists
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Set a new timeout for the debounced action
      debounceTimeoutRef.current = setTimeout(
        () => func.apply(this, args),
        wait
      );
    };
  }

  const handleTitleChange = useCallback(
    debounce((val) => {
      dispatch(setWheelName(val)); // Dispatch action to update wheel name
      dispatch(pushToHistoryAction()); // Push the new state to history
    }, 500),
    []
  );

  const handleSubtitleChange = useCallback(
    debounce((val) => {
      dispatch(setSubTitle(val)); // Dispatch action to update subtitle
      dispatch(pushToHistoryAction()); // Push the new state to history
    }, 500),
    []
  );

  const handleDescriptionChange = useCallback(
    debounce((val) => {
      dispatch(setDescription(val)); // Dispatch action to update description
      dispatch(pushToHistoryAction()); // Push the new state to history
    }, 500),
    []
  );

  // ISP-9999 (stgport-education-category-discover-wheel-tags): staging had no
  // Categories/Tags UI at all -- this is a minimal port of dev's builder
  // picker, not the full multi-select/autocomplete dev later iterated on.
  const handleCategoryToggle = (slug) => (event) => {
    const isChecked = event.target.checked;
    let next;
    if (isChecked) {
      if (categories.length >= MAX_WHEEL_CATEGORIES) return;
      next = [...categories, slug];
    } else {
      next = categories.filter((c) => c !== slug);
    }
    dispatch(setCategories(next));
    dispatch(pushToHistoryAction());
  };

  const commitTagInput = () => {
    const value = tagInputValue.trim();
    setTagInputValue("");
    if (!value) return;
    const alreadyExists = wheel_tags.some(
      (tag) => tag.name.toLowerCase() === value.toLowerCase()
    );
    if (alreadyExists) return;
    dispatch(setWheelTags([...wheel_tags, { name: value }]));
    dispatch(pushToHistoryAction());
  };

  const handleTagInputKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commitTagInput();
    }
  };

  const handleRemoveTag = (name) => () => {
    dispatch(setWheelTags(wheel_tags.filter((tag) => tag.name !== name)));
    dispatch(pushToHistoryAction());
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", marginBottom: "20px" }}
    >
      <FroalaTextEditor
        editorHeight={"80px"}
        name={"text_body_1"}
        id={1}
        value={wheel_name === "Untitled Wheel" ? "" : wheel_name}
        onChange={handleTitleChange}
        handleEditorCustomization={handleFroalaCustomization}
        editorRef={editorRef}
        label={"Wheel Name"}
        toolbarButtons={["|", "emoticons", "specialCharacters", "aiAssistant"]}
        type="title"
        characters="75"
        focused={froalaFocus === FROALA_FOCUS_ENUM.TITLE}
        placeholder="Untitled Wheel"
        dataEditorId="1"
      />

      <FroalaTextEditor
        placeholder="Subtitle"
        editorHeight={"80px"}
        name={"text_body_2"}
        id={2}
        value={sub_title}
        onChange={handleSubtitleChange}
        handleEditorCustomization={handleFroalaCustomization}
        editorRef={editorRef2}
        label={"Subtitle"}
        toolbarButtons={["|", "emoticons", "specialCharacters", "aiAssistant"]}
        type="title"
        characters="100"
        focused={froalaFocus === FROALA_FOCUS_ENUM.SUBTITLE}
        dataEditorId="2"
      />

      <FroalaTextEditor
        placeholder="Describe your Wheel"
        editorHeight={"150px"}
        name={"text_body_3"}
        id={3}
        value={description}
        onChange={handleDescriptionChange}
        handleEditorCustomization={handleFroalaCustomization}
        editorRef={editorRef3}
        label={"description"}
        dataEditorId="3"
      />

      <div style={{ marginTop: "16px" }}>
        <label style={{ fontWeight: 600, display: "block", marginBottom: "8px" }}>
          Categories
        </label>
        <FormGroup row data-testid="wheel-categories-group">
          {WHEEL_CATEGORY_OPTIONS.map(({ slug, label }) => (
            <FormControlLabel
              key={slug}
              control={
                <Checkbox
                  size="small"
                  checked={categories.includes(slug)}
                  onChange={handleCategoryToggle(slug)}
                  inputProps={{ "data-testid": `wheel-category-checkbox-${slug}` }}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>
      </div>

      <div style={{ marginTop: "16px" }}>
        <label style={{ fontWeight: 600, display: "block", marginBottom: "8px" }}>
          Tags
        </label>
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}
          data-testid="wheel-tags-chip-list"
        >
          {wheel_tags.map((tag) => (
            <Chip
              key={tag.name}
              label={tag.name}
              size="small"
              onDelete={handleRemoveTag(tag.name)}
            />
          ))}
        </div>
        <TextField
          size="small"
          fullWidth
          placeholder="Add a tag and press Enter"
          value={tagInputValue}
          onChange={(e) => setTagInputValue(e.target.value)}
          onKeyDown={handleTagInputKeyDown}
          onBlur={commitTagInput}
          inputProps={{ "data-testid": "wheel-tag-input" }}
        />
      </div>

      {showAiPrompt &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              zIndex: 6522299,
              right: aiPromptPosition.right,
              top: aiPromptPosition.top,
              overflow: "auto",
            }}
          >
            <NewAIPrompt
              modalWidth="350px"
              completionType={currentCompletionType}
              onCloseIconPress={() => setShowAiPrompt(false)}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
